import { useEffect } from 'react';
import { getExtensionId } from '@/config/env-config';
import { useAuth } from '@/hooks/use-auth';
import { useApplications } from '@/hooks/use-user-portal';
import type {
  ExtensionSyncRequest,
  ExtensionSyncResponse,
} from '@/types/extension-sync.types';
import { buildAppLaunchPath } from '@/utils/app-launch-utils';
import { logError } from '@/utils/error-logger';
import { getApplicationIconUrl } from '@/utils/icon-utils';

export function useExtensionSync() {
  const { user, tenantSlug } = useAuth();
  const { data: apps } = useApplications();

  useEffect(() => {
    if (typeof chrome === 'undefined' || !chrome.runtime?.sendMessage) {
      return;
    }

    if (!user || !tenantSlug || !apps) {
      return;
    }

    const extensionId = getExtensionId();

    if (!extensionId) {
      return;
    }

    const payload: ExtensionSyncRequest = {
      action: 'syncUserData',
      user: { name: user.name, email: user.email, tenantSlug: tenantSlug },
      apps: apps.items.map(app => ({
        id: app.app_id,
        name: app.app_name,
        iconUrl: getApplicationIconUrl(app.icon_path, app.app_name),
        ssoType: app.sso_type,
        launchUrl: `${window.location.origin}${buildAppLaunchPath(app)}`,
      })),
    };

    chrome.runtime.sendMessage(
      extensionId,
      payload,
      (response?: ExtensionSyncResponse) => {
        // lastError means no reachable receiver (extension disabled or outdated) — expected, not an error.
        if (chrome.runtime.lastError) {
          return;
        }

        if (response?.status === 'error') {
          logError(response.error, 'extension-sync');
        }
      }
    );
  }, [user, apps, tenantSlug]);
}
